import { ThumbsUp32 as ThumbsUpOutlined } from '@carbon/icons-react';
import { CompanyClientDataType } from '@upright/common/model/company-client-data/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { getHoverColor } from '@upright/ui/common/generic/global/styling-helpers';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { isLikedAnonymously, saveAnonymousLike } from '@upright/ui/common/generic/utilities/storage';
import {
  useDeleteCompanyClientDataReaction,
  usePostCompanyClientDataReaction,
} from '@upright/ui/common/upright/company-client-data/query-hooks/reaction-query-hooks';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import ThumbsUpFilled from './carbon-thumbs-up--filled.svg';

interface Props {
  clientDataType: CompanyClientDataType;
  clientDataId: number;
  companyId: string;
  likes?: number;
  liked?: boolean;
}

export function InfoBoxLikeButton(props: Props) {
  const { clientDataType, clientDataId, companyId } = props;

  // TODO: Create a proper error display for wrong data instead of defaulting to 0/false.
  const likes = props.likes || 0;
  const liked = !!props.liked;
  const user = useGetLoggedInUser();
  const [likedAnonymously, setLikedAnonymously] = useState<boolean>(
    isLikedAnonymously(companyId, clientDataType, clientDataId),
  );
  useEffect(() => {
    if (likedAnonymously !== isLikedAnonymously(companyId, clientDataType, clientDataId)) {
      saveAnonymousLike(likedAnonymously, companyId, clientDataType, clientDataId);
    }
  }, [companyId, clientDataType, clientDataId, likedAnonymously]);
  const post = usePostCompanyClientDataReaction(companyId, clientDataType, clientDataId);
  const del = useDeleteCompanyClientDataReaction(companyId, clientDataType, clientDataId);
  const isLiked = user ? liked : likedAnonymously;
  const Icon = isLiked ? ThumbsUpFilled : ThumbsUpOutlined;

  // Work around browser caching refusing fetch fresh anonymoys like without disabling caching
  const estimatedLikes = !user && isLiked ? likes + 1 : likes;

  return (
    <Root
      onClick={() => {
        const newLiked = !isLiked;
        if (!user) {
          setLikedAnonymously(newLiked);
        }
        if (newLiked) {
          post.mutate();
        } else {
          del.mutate();
        }
      }}
    >
      <Icon
        style={{
          fill: 'currentcolor',
          height: '1.2em',
          width: '1.2em',
          marginRight: '0.4em',
        }}
      />
      <span style={{ marginRight: '0.4em' }}>Like</span>
      {estimatedLikes > 0 && <span style={{ color: BouColor.TOOL_TEXT_DARK_GRAY }}>({estimatedLikes})</span>}
    </Root>
  );
}

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  &:hover {
    cursor: pointer;
    color: ${getHoverColor()};
  }
`;
